import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ProfileHeaderEdit } from "@/components/profile/ProfileHeaderEdit";
import { StatsGrid } from "@/components/profile/StatsGrid";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useState, useEffect } from "react";
import { useToast } from "@/components/ui/use-toast";
import { Send } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { FollowersDialog } from "@/components/profile/FollowersDialog";
import { supabase } from "@/lib/supabase";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Post } from "@/components/post/Post";
import { useParams, useNavigate } from "react-router-dom";
import { ProfileHeader } from "@/components/profile/ProfileHeader";
import { ProfileBadges } from "@/components/profile/ProfileBadges";
import { useProfile } from "@/hooks/useProfile";

const Profile = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const maxLength = 250;
  const [followersDialogOpen, setFollowersDialogOpen] = useState(false);
  const [followingDialogOpen, setFollowingDialogOpen] = useState(false);
  const { profile: userProfile } = useProfile();

  useEffect(() => {
    if (!id && userProfile?.username) {
      navigate(`/profile/${userProfile.username}`);
    }
  }, [id, userProfile, navigate]);

  const formatUsername = (username: string) => {
    return username.toLowerCase().replace(/\s+/g, '_');
  };

  const { data: profileData, isLoading: profileLoading } = useQuery({
    queryKey: ["profile", id],
    queryFn: async () => {
      if (!id) {
        throw new Error("No profile ID available");
      }

      console.log("Fetching profile for ID:", id);
      const formattedId = formatUsername(id);
      
      const { data: profileByUsername, error: usernameError } = await supabase
        .from("profiles")
        .select("*")
        .eq("username", formattedId)
        .maybeSingle();

      if (profileByUsername) {
        console.log("Found profile by username:", profileByUsername);
        return {
          ...profileByUsername,
          username: formatUsername(profileByUsername.username)
        };
      }

      const { data: profileByUUID, error: uuidError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", id)
        .maybeSingle();

      if (uuidError || !profileByUUID) {
        console.error("Profile not found:", { usernameError, uuidError });
        throw new Error("Profile not found");
      }

      console.log("Found profile by UUID:", profileByUUID);
      return {
        ...profileByUUID,
        username: formatUsername(profileByUUID.username)
      };
    },
    enabled: !!id,
    retry: false,
    meta: {
      errorMessage: "Could not fetch profile",
    }
  });

  const isOwnProfile = user?.id === profileData?.id;

  const { data: posts, isLoading: postsLoading } = useQuery({
    queryKey: ["profile-posts", profileData?.id],
    queryFn: async () => {
      if (!profileData?.id) return [];

      const { data, error } = await supabase
        .from("posts")
        .select(`
          *,
          profiles (
            username,
            full_name,
            avatar_url
          )
        `)
        .eq("user_id", profileData.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!profileData?.id,
  });

  const createPost = useMutation({
    mutationFn: async (content: string) => {
      if (!user) throw new Error("Must be logged in to post");
      
      const { error } = await supabase
        .from("posts")
        .insert([
          {
            content,
            user_id: user.id,
          },
        ]);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile-posts"] });
      setPrompt("");
      toast({
        title: "Success",
        description: "Your post has been published!",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = () => {
    if (prompt.trim().length === 0) {
      toast({
        title: "Error",
        description: "Your prompt cannot be empty",
        variant: "destructive",
      });
      return;
    }

    createPost.mutate(prompt);
  };

  if (profileLoading) {
    return (
      <div className="min-h-screen bg-flux-black text-white p-8">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse">
            <div className="h-48 bg-flux-cyan/20 rounded-xl mb-8"></div>
            <div className="h-32 w-32 bg-flux-cyan/20 rounded-full mb-4"></div>
            <div className="h-8 bg-flux-cyan/20 rounded w-1/4 mb-2"></div>
            <div className="h-4 bg-flux-cyan/20 rounded w-1/3 mb-8"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="min-h-screen bg-flux-black text-white p-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-2xl font-bold text-flux-cyan mb-4">Profile not found</h1>
          <p className="text-gray-400">The profile you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="min-h-screen bg-flux-black text-white p-8"
    >
      <div className="max-w-4xl mx-auto space-y-8">
        {isOwnProfile ? (
          <ProfileHeaderEdit />
        ) : (
          <ProfileHeader profile={{
            ...profileData,
            username: formatUsername(profileData.username)
          }} />
        )}
        <StatsGrid />

        <Card className="bg-flux-black/50 border border-flux-cyan/20">
          <CardContent className="p-6">
            <Tabs defaultValue="posts" className="w-full">
              <TabsList className="grid w-full grid-cols-3 bg-flux-black/50">
                <TabsTrigger value="posts" className="text-white data-[state=active]:bg-flux-cyan/20">
                  Posts
                </TabsTrigger>
                <TabsTrigger value="likes" className="text-white data-[state=active]:bg-flux-cyan/20">
                  Likes
                </TabsTrigger>
                <TabsTrigger value="badges" className="text-white data-[state=active]:bg-flux-cyan/20">
                  Badges
                </TabsTrigger>
              </TabsList>

              <TabsContent value="posts" className="mt-4">
                {isOwnProfile && (
                  <div className="mb-6 p-4 rounded-xl bg-flux-black/50 border border-flux-cyan/20 backdrop-blur-xl">
                    <div className="space-y-4">
                      <Textarea
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value.slice(0, maxLength))}
                        placeholder="What's on your mind?"
                        className="bg-transparent border-flux-cyan/20 focus-visible:ring-flux-cyan/30 resize-none text-white"
                        rows={3}
                      />
                      <div className="flex justify-between items-center">
                        <span className={`text-sm ${prompt.length === maxLength ? 'text-red-400' : 'text-gray-400'}`}>
                          {prompt.length}/{maxLength} characters
                        </span>
                        <Button 
                          onClick={handleSubmit}
                          className="bg-flux-black hover:bg-flux-black/80 text-flux-cyan border border-flux-cyan"
                          disabled={prompt.length === 0 || createPost.isPending}
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Post
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                <div className="space-y-4">
                  {posts?.map((post) => (
                    <Post
                      key={post.id}
                      post={post}
                      currentUserId={user?.id}
                    />
                  ))}
                  {posts?.length === 0 && (
                    <div className="text-center text-gray-400">
                      No posts yet
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="likes" className="mt-4">
                <div className="text-center text-gray-400">
                  Likes feature coming soon
                </div>
              </TabsContent>

              <TabsContent value="badges" className="mt-4">
                {profileData && (
                  <ProfileBadges userId={profileData.id} isOwnProfile={isOwnProfile} />
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      <FollowersDialog
        isOpen={followersDialogOpen}
        onClose={() => setFollowersDialogOpen(false)}
        title="Followers"
        profileId={profileData?.id}
        type="followers"
      />
      <FollowersDialog
        isOpen={followingDialogOpen}
        onClose={() => setFollowingDialogOpen(false)}
        title="Following"
        profileId={profileData?.id}
        type="following"
      />
    </motion.div>
  );
};

export default Profile;
